import { Tab as BaseTab, Box, styled, Tabs } from '@mui/material'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dynamicClassName } from 'styles/helper'
import { colors } from 'styles/variables'
import { ArrowDatePicker } from 'ui/molecules/pickers/ArrowDatePicker/ArrowDatePicker'
import Datetime from 'utils/datetime/datetime'
import NotificationFilterButton from './components/NotificationFilterButton/NotificationFilterButton'
import { OptimizationLogPreferenceTooltip } from './components/OptimizationLogPreferenceTooltip/OptimizationLogPreferenceTooltip'
import { useOptimizationLogVisibility } from './hooks/useOptimizationLogVisibility'
import {
  eventFilterLabels,
  eventFilters,
  optimizationFilterLabels,
  optimizationFilters,
} from './NotificationCenter.helper'
import styles from './NotificationCenter.module.less'
import NotificationEventTab from './NotificationEventTab'
import NotificationOptimizationsTab from './NotificationOptimizationsTab'

const Tab = styled(BaseTab)({
  textTransform: 'uppercase',
})

function a11yProps(index: number): Record<string, string> {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

export const EVENT_TAB_INDEX = 1
export const OPTIMIZATION_TAB_INDEX = 0

export default function NotificationCenter(): ReactElement {
  const [value, setValue] = React.useState(0)
  const [date, setDate] = useState<ISODateTime>(Datetime.getISONow())
  const { t } = useTranslation()

  const [selectedFilters, setSelectedFilters] = useState<string[]>([])

  const hasEventPermission = useHasPermission('view_operational_events')
  const { showNewOptimizationLogs } = useOptimizationLogVisibility()

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setValue(newValue)
    setSelectedFilters([]) // Clear all filters when switching tabs
  }
  // The Events tab always renders the legacy notification item layout, which has no internal
  // max-width of its own, so it needs the narrow panel regardless of the opt-job log feature flag.
  const showsLegacyLayout = value === EVENT_TAB_INDEX || !showNewOptimizationLogs
  return (
    <div
      className={dynamicClassName(
        { [styles.NotificationCenter__legacyWidth]: showsLegacyLayout },
        styles.NotificationCenter
      )}
    >
      <div className={styles.NotificationCenter_Header}>
        <div className={styles.NotificationCenter_Header_Title}>{t('Log')}</div>
        <div className={styles.NotificationCenter_Header_CalendarAndFilter}>
          <div className={styles.NotificationCenter_Header_CalendarContainer}>
            <ArrowDatePicker
              theme="dark"
              prevButtonTooltip={t(`Previous day`)}
              nextButtonTooltip={t(`Next day`)}
              format={`ddd D MMM`}
              step={{ unit: 'day', amount: 1 }}
              value={moment(date)}
              onChange={(date) => setDate(Datetime.toISOString(date))}
              maxValue={moment(Datetime.getTodayDate().endTime)}
            />
          </div>
          {value === EVENT_TAB_INDEX && (
            <NotificationFilterButton
              tooltipTitle={t('Filter events')}
              allFilters={eventFilters}
              allFilterLabels={eventFilterLabels}
              handleFilterChange={(filter) => setSelectedFilters(filter)}
            />
          )}
          {value === OPTIMIZATION_TAB_INDEX && (
            <NotificationFilterButton
              tooltipTitle={t('Filter optimizations')}
              allFilters={optimizationFilters}
              allFilterLabels={optimizationFilterLabels}
              handleFilterChange={(filter) => setSelectedFilters(filter)}
            />
          )}
        </div>
      </div>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: '65px',
            backgroundColor: colors.primary7,
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="basic tabs example"
              centered
              indicatorColor="secondary"
              textColor="inherit"
            >
              <Tab value={OPTIMIZATION_TAB_INDEX} label={t('Optimizations')} {...a11yProps(OPTIMIZATION_TAB_INDEX)} />
              {hasEventPermission && (
                <Tab value={EVENT_TAB_INDEX} label={t('Events')} {...a11yProps(EVENT_TAB_INDEX)} />
              )}
            </Tabs>
            {value === OPTIMIZATION_TAB_INDEX && <OptimizationLogPreferenceTooltip />}
          </Box>
        </Box>
        {value === EVENT_TAB_INDEX && hasEventPermission && (
          <NotificationEventTab selectedFilters={selectedFilters} date={date} />
        )}
        {value === OPTIMIZATION_TAB_INDEX && (
          <NotificationOptimizationsTab selectedFilters={selectedFilters} date={date} />
        )}
      </Box>
    </div>
  )
}
